<template>
    <div class="sortable-table-wrapper">
        <CustomTable 
            :style="tableStyle" 
            :scrollable="scrollable" 
            :bordered="bordered"
            :class="{ 'dark-table': theme === THEMES.DARK }"
        >
            <template #head>
                <tr>
                    <th 
                        v-for="column in columns" 
                        :key="column.key"
                        :class="['sortable-header', { 
                            'sortable': column.sortable !== false,
                            'sorted-asc': sortBy === (column.sortKey || column.key) && sortOrder === 'asc',
                            'sorted-desc': sortBy === (column.sortKey || column.key) && sortOrder === 'desc'
                        }]"
                        @click="column.sortable !== false ? sort(column.sortKey || column.key) : null"
                        :style="column.width ? { minWidth: column.width } : {}"
                    >
                        <div class="header-content">
                            <span>{{ column.title }}</span>
                            <div class="sort-icons" v-if="column.sortable !== false">
                                <i class="material-icons sort-icon" 
                                   :class="{ active: sortBy === (column.sortKey || column.key) && sortOrder === 'asc' }">
                                   keyboard_arrow_up
                                </i>
                                <i class="material-icons sort-icon" 
                                   :class="{ active: sortBy === (column.sortKey || column.key) && sortOrder === 'desc' }">
                                   keyboard_arrow_down
                                </i>
                            </div>
                        </div>
                    </th>
                </tr>
            </template>
            <template #body>
                <slot name="body" :sortedData="sortedData">
                    <tr v-for="(item, index) in sortedData" :key="getItemKey(item, index)">
                        <td v-for="column in columns" :key="column.key" :style="column.width ? { minWidth: column.width } : {}">
                            <slot 
                                :name="`cell-${column.key}`" 
                                :item="item" 
                                :value="getNestedValue(item, column.key)"
                                :index="index"
                            >
                                {{ formatCellValue(item, column) }}
                            </slot>
                        </td>
                    </tr>
                </slot>
            </template>
        </CustomTable>
    </div>
</template>

<script>
import CustomTable from "../layout/CustomTable.vue"
import { mapState } from "vuex"
import { formatBusinessDateOnly, formatBusinessInstant, isBusinessDate } from "../../utils/business-date"

export default {
    name: "SortableTable",
    components: {
        CustomTable
    },
    props: {
        data: {
            type: Array,
            required: true
        },
        columns: {
            type: Array,
            required: true
        },
        defaultSort: {
            type: String,
            default: ""
        },
        defaultSortOrder: {
            type: String,
            default: "asc",
            validator: value => ["asc", "desc"].includes(value)
        },
        tableStyle: {
            type: String,
            default: "font-size: 12px; max-width: unset; min-width: 100%; width: unset"
        },
        scrollable: {
            type: Boolean,
            default: true
        },
        bordered: {
            type: Boolean,
            default: true
        },
        itemKey: {
            type: String,
            default: "id"
        }
    },
    data() {
        return {
            sortBy: this.defaultSort,
            sortOrder: this.defaultSortOrder
        }
    },
    watch: {
        defaultSort(value) {
            this.sortBy = value
        },
        defaultSortOrder(value) {
            this.sortOrder = value
        }
    },
    computed: {
        ...mapState("misc", ["theme", "THEMES"]),
        
        sortedData() {
            if (!this.sortBy || !this.data.length) {
                return this.data
            }
            
            return [...this.data].sort((a, b) => {
                const aValue = this.getNestedValue(a, this.sortBy)
                const bValue = this.getNestedValue(b, this.sortBy)
                
                // Handle null/undefined values
                if (aValue == null && bValue == null) return 0
                if (aValue == null) return this.sortOrder === 'asc' ? 1 : -1
                if (bValue == null) return this.sortOrder === 'asc' ? -1 : 1
                
                // Handle numeric values
                if (this.isNumeric(aValue) && this.isNumeric(bValue)) {
                    const numA = parseFloat(aValue.toString().replace(/[^0-9.-]/g, ''))
                    const numB = parseFloat(bValue.toString().replace(/[^0-9.-]/g, ''))
                    return this.sortOrder === 'asc' ? numA - numB : numB - numA
                }
                
                // Handle date values
                if (this.isDate(aValue) || this.isDate(bValue)) {
                    const dateA = new Date(aValue)
                    const dateB = new Date(bValue)
                    return this.sortOrder === 'asc' ? dateA - dateB : dateB - dateA
                }
                
                // Handle string values
                const strA = aValue.toString().toLowerCase()
                const strB = bValue.toString().toLowerCase()
                
                if (strA < strB) return this.sortOrder === 'asc' ? -1 : 1
                if (strA > strB) return this.sortOrder === 'asc' ? 1 : -1
                return 0
            })
        }
    },
    methods: {
        sort(columnKey) {
            if (this.sortBy === columnKey) {
                // Toggle sort order
                this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
            } else {
                // New column, default to ascending
                this.sortBy = columnKey
                this.sortOrder = 'asc'
            }
            
            this.$emit('sort', {
                column: columnKey,
                order: this.sortOrder
            })
        },
        
        getNestedValue(obj, path) {
            return path.split('.').reduce((current, key) => {
                return current && current[key] !== undefined ? current[key] : null
            }, obj)
        },
        
        formatCellValue(item, column) {
            const value = this.getNestedValue(item, column.key)
            
            if (value == null) return ''
            
            // Apply column-specific formatting
            if (column.formatter && typeof column.formatter === 'function') {
                return column.formatter(value, item)
            }
            
            // Default formatting based on column type
            if (column.type === 'currency') {
                return `$${parseFloat(value).toFixed(2)}`
            }
            
            if (column.type === 'date') {
                return isBusinessDate(value)
                    ? formatBusinessDateOnly(value)
                    : formatBusinessInstant(value, { dateOnly: true })
            }
            
            if (column.type === 'datetime') {
                return formatBusinessInstant(value)
            }
            
            return value
        },
        
        isNumeric(value) {
            if (typeof value === 'number') return true
            if (typeof value === 'string') {
                // Check if string looks like a number (including currency)
                const cleaned = value.replace(/[^0-9.-]/g, '')
                return cleaned !== '' && !isNaN(parseFloat(cleaned))
            }
            return false
        },
        
        isDate(value) {
            if (value instanceof Date) return true
            if (typeof value === 'string') {
                // Simple date pattern check
                return /\d{4}-\d{2}-\d{2}/.test(value) || /\d{2}\/\d{2}\/\d{4}/.test(value)
            }
            return false
        },
        
        getItemKey(item, index) {
            return item[this.itemKey] || index
        }
    }
}
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";
.sortable-table-wrapper {
    .sortable-header {
        cursor: pointer;
        user-select: none;
        position: relative;
        transition: background-color 0.2s ease;
        
        &.sortable:hover {
            background-color: rgba(0, 0, 0, 0.05);
        }
        
        &.sorted-asc, &.sorted-desc {
            background-color: rgba(13, 45, 44, 0.1);
        }
        
        .dark-table & {
            &.sortable:hover {
                background-color: rgba(255, 255, 255, 0.05);
            }
            
            &.sorted-asc, &.sorted-desc {
                background-color: rgba(186, 142, 88, 0.1);
            }
        }
    }
    
    .header-content {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        min-height: 20px;
    }
    
    .sort-icons {
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-left: auto;
        opacity: 0.5;
        transition: opacity 0.2s ease;
        
        .sortable-header:hover & {
            opacity: 0.8;
        }
        
        .sortable-header.sorted-asc &,
        .sortable-header.sorted-desc & {
            opacity: 1;
        }
    }
    
    .sort-icon {
        font-size: 16px;
        line-height: 1;
        color: rgba($textColor, 0.7);
        transition: color 0.2s ease;
        margin: -2px 0;
        
        &.active {
            color: $primaryColor;
        }
        
        .dark-table & {
            color: rgba($darkTextColor, 0.7);
            
            &.active {
                color: $secondaryColorDark;
            }
        }
    }
}
</style> 
